import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { tenantOrganizationKeys } from '@/api/query-keys';
import type {
  ShowTenantOrganizationResponse,
  UpdateTenantOrganizationRequest,
  UpdateTenantOrganizationResponse,
  Organization
} from '@bliks-io/contracts';

/**
 * Fetch a tenant's organization data (superadmin only)
 */
export function useTenantOrganization(tenantId: string) {
  return useQuery({
    queryKey: tenantOrganizationKeys.detail(tenantId),
    queryFn: async ({ signal }): Promise<Organization> => {
      const { data } = await apiClient.get<ShowTenantOrganizationResponse>(
        `/api/v2/tenants/${tenantId}/organization`,
        { signal }
      );
      return data.data;
    },
    enabled: !!tenantId
  });
}

/**
 * Update a tenant's organization settings (superadmin only)
 * Currently only allows updating enableAI
 */
export function useUpdateTenantOrganization(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: UpdateTenantOrganizationRequest): Promise<Organization> => {
      const { data } = await apiClient.patch<UpdateTenantOrganizationResponse>(
        `/api/v2/tenants/${tenantId}/organization`,
        updates
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantOrganizationKeys.detail(tenantId) });
    }
  });
}
